/**
 * PATH: packages/email-hub/src/feature/components/inbox-command/contracts.ts
 * FILE_ID: EMAIL-INBOX-CMD-CONTRACT-001
 * PURPOSE: Pure responsive and loaded-selection contracts for Inbox commands.
 * WHY: Width tiers and bulk scope must be deterministic before UI extraction.
 * FLOW: border-box width/visible ids -> stable tier and safe selection.
 */

export const INBOX_COMMAND_STANDARD_MIN_PX = 360
export const INBOX_COMMAND_ROOMY_MIN_PX = 520

export type InboxCommandDensity = 'compact' | 'standard' | 'roomy'

export function resolveInboxCommandDensity(
  borderBoxWidth: number,
): InboxCommandDensity {
  if (!Number.isFinite(borderBoxWidth) || borderBoxWidth < 0) {
    return 'compact'
  }
  if (borderBoxWidth < INBOX_COMMAND_STANDARD_MIN_PX) return 'compact'
  if (borderBoxWidth < INBOX_COMMAND_ROOMY_MIN_PX) return 'standard'
  return 'roomy'
}

export function uniqueLoadedEmailIds(
  visibleIds: readonly string[],
): string[] {
  const seen = new Set<string>()
  const ids: string[] = []

  for (const rawId of visibleIds) {
    const id = rawId.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }

  return ids
}

export function reconcileLoadedSelection(
  selectedIds: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const visible = new Set(uniqueLoadedEmailIds(visibleIds))
  return new Set([...selectedIds].filter((id) => visible.has(id)))
}

export function selectEveryLoadedEmail(
  visibleIds: readonly string[],
): Set<string> {
  return new Set(uniqueLoadedEmailIds(visibleIds))
}

export function isEveryLoadedEmailSelected(
  selectedIds: ReadonlySet<string>,
  visibleIds: readonly string[],
): boolean {
  const loaded = uniqueLoadedEmailIds(visibleIds)
  return loaded.length > 0 && loaded.every((id) => selectedIds.has(id))
}

/** What the funnel badge is allowed to count.
 *
 *  Every field must read false/0 on an inbox nobody has touched. The
 *  previous shape counted `hasVisibilityFilter`, which was
 *  `hidePromotions || hideCleared` — both default to TRUE, so an untouched
 *  inbox permanently showed "1" and the badge stopped carrying any signal.
 *  Standing preferences are not refinements: they live in Refine and are
 *  modelled as FilterPill `kind: 'standing'`, which this count excludes.
 *
 *  `activeFilterPills` is the number of `kind: 'filter'` pills, so the
 *  badge and the visible pill strip can never disagree about how many
 *  filters are on. It replaces `selectedCategoryCount` +
 *  `hasAdvancedFilters`, which double-counted allocation and categories. */
export interface InboxRefinementState {
  activeFilterPills: number
  hasNonDefaultOrder: boolean
  groupByMailbox: boolean
  smartSearch: boolean
}

export function countActiveRefinements(
  state: InboxRefinementState,
): number {
  return (
    Math.max(0, state.activeFilterPills) +
    Number(state.hasNonDefaultOrder) +
    Number(state.groupByMailbox) +
    Number(state.smartSearch)
  )
}
